import { Breakpoints } from '@angular/cdk/layout';
import { PipeTransform, TemplateRef } from '@angular/core';
import {PageEvent} from '@angular/material/paginator';
import { ContextmenuType, TableColumn as NgxTableColumn } from '@swimlane/ngx-datatable';

import { Observable } from 'rxjs';

import { HyphenPipe } from '@atmi/shared/pipes/hyphen.pipe';

import { QueryBuilder } from '@ui/filters/filters';

export interface Page<T> {
  data: T[] | [];
  count: number;
}

export type TableSortType = Array<{ field: string, type: 'asc' | 'desc' }>;
export type TableFilterType = QueryBuilder<any>;

export type PagerFn<T> = (page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>  Observable<Page<T>>;

export interface UiTableComponent<T> {
  pager$: PagerFn<T> | Observable<PagerFn<T>>;
  fields?: Array<TableField<T>>;
  fields$?: Observable<Array<TableField<T>>>;
}

export type TemplateField = 'actions' | 'context' | 'delete' | 'show' | 'edit' | 'link' | 'img' | 'async' | 'date-time' | 'date';
export type Align = 'center' | 'left' | 'right';

export interface TableInputs<T> {
  data: T[];
  fields: any;
  settings: any;
}

// FIXME: [typing] - This internal interface depends directly form ngx-databale. Avoid this!!!
export type TableRow<T> = T & { $$id: string };

export type TableCustomColumnComponent<T> = T;

export interface TableSelection<T> {
  selected: T[];
}

export interface TableField<T> {
  /** Set the header title */
  header: string;
  /** Set a defined component to use in a column */
  template?: TemplateField;
  /** Set a key used to map a field with the related column */
  key: keyof T | string;
  context?: (value: T) => any;
  click?: (value: TableRow<T>) => void;
  align?: Align;
  customComponent?: any;
  selectable?: boolean;
  transformPipe?: PipeTransform;
  sortable?: boolean;
  /** The default width of the column, in pixels */
  width?: number;
  /** The values to use are from Breakpoints in @angular/cdk/layout or an equivalent format string */
  hideBreakpoints?: string[];
  /** The values to use are from Breakpoints in @angular/cdk/layout or an equivalent format string */
  showBreakpoints?: string[];
}

export interface TableColumn<T> {
  key: keyof T;
}

export interface TableContextMenu<T> {
  label: string;
  action: (data: T) => void;
}

export type TablePageEvent = PageEvent;

export const EMPTY_TABLE_SOURCE = { settings: [], fields: [], data: [] };

export function transformFields<R extends T, T>(
  fields: Array<TableField<R>>,
  templates: Record<string, TemplateRef<any>>,
  defaultPipe = new HyphenPipe(),
) {
  return fields.map((f) => {
    const field: NgxTableColumn = {
      $$valueGetter: f.context || undefined,
      canAutoResize: f.selectable ? false : true,
      cellClass: (f.click && !f.template) ? `${f.align || ''} clickable-cell` : f.align,
      cellTemplate: f.template ? templates[f.template] : undefined,
      checkboxable: f.selectable || false,
      headerCheckboxable: f.selectable || false,
      name: f.header,
      pipe: (f.template || f.selectable) ? undefined : (f.transformPipe || defaultPipe),
      prop: f.key as string,
      sortable: f.sortable || false,
      width: f.width ? f.width : 50, // Not used with 'force' columnMode
    };

    return field;
  });
}

export function transformColumnSetting<T>(settings: Array<TableColumn<T>>) {
  return settings.map((s) => {
    const setting: any = {
      objectKey: s.key as string,
    };
    return setting;
  });
}

export function getSimpleColumnSettings<T>(fields: Array<TableField<T>>): any[] { // GtConfigSetting[] {
  return fields.map((f) => ({ objectKey: f.key as string }));
}

export function getColumnClass(align?: Align) {
  if (!align) {
    return '';
  }

  switch (align) {
    case 'center':
      return 'position-center';
    case 'left':
      return 'position-left';
    case 'right':
      return 'position-right';
  }
}

// tslint:disable-next-line:variable-name
export function isContextMenuOnBody<T>(type: ContextmenuType, _content: NgxTableColumn | T): _content is T {
  if (type === ContextmenuType.body) {
    return true;
  }
  return false;
}

/**
 * Convert an array into a map, mapping each element with a projection function
 *
 * @param array the array
 * @param key the name of the field that will be used as key
 * @param proj the function used to transform the elements
 */
export function projectToMap<T extends Record<K, string>, K extends keyof T, U>(array: T[], key: K, proj: (x: T) => U) {
  return array.reduce((base, v) => {
    const id = v[key];
    base[id] = proj(v);
    return base;
  }, {} as Record<T[K], U>);
}

export const basicDeleteTemplateOptions: TableField<any> = {
  align: 'center',
  header: '',
  key: 'delete',
  template: 'delete',
  width: -70,
};

export const basicEditTemplateOptions: TableField<any> = {
  align: 'center',
  header: '',
  key: 'edit',
  template: 'edit',
  width: -70,
};

export const basicShowTemplateOptions: TableField<any> = {
  align: 'center',
  header: '',
  key: 'show',
  template: 'show',
  width: -70,
};
